//External libraries
import * as React from "react";
import { gql, useQuery } from "@apollo/client";
import { observer } from "mobx-react";
import { useLocalization } from "@shared-ui/localization-context";
import { useBexApi } from "@shared-ui/bex-api-context";

// Uitk
import { UitkLink } from "uitk-react-link";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading } from "uitk-react-text";

// Components-Utilities
import IsomorphicCarousel from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { RecentlyViewedHotelCard } from "./components/RecentlyViewedHotelCard";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { normalizeOptionsData } from "src/components/flexFramework/FlexTemplateNode";
import { buildVariablesList } from "./utils/buildVariablesList";
import { groupHotelsByDate } from "./utils/groupHotelsByDate";
import { withStores } from "src/stores";

// Types
import {
  RecentlyViewedHotelsProps,
  RecentlyViewedHotelsFlexModuleResult,
  CustomerSearchHistoryResponse,
  CustomerSearchHistoryQueryComponentProps,
  HotelsGroupedByDates,
  HistoryItem,
  RecentlyViewedHotelsCardQueryComponentProps,
  CustomerPropertySearchByIdResponse,
} from "./typings";
import { customerSearchHistoryQueryQuery, customerPropertySearchByIdQueryQuery, HistoryType } from "__generated__/api";

const customerSearchHistoryQuery = gql`
  ${customerSearchHistoryQueryQuery}
`;

const NUMBER_OF_HOTELS_TO_GET = 6;

export const RecentlyViewedHotels = withStores(
  "flexModuleModelStore",
  "hotels"
)(
  observer((props: RecentlyViewedHotelsProps) => {
    const { flexTemplateNode, flexModuleModelStore, hotels: hotelsStore } = props;
    const { id } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(id) as RecentlyViewedHotelsFlexModuleResult | null;

    /* istanbul ignore next */
    if (!model) {
      return null;
    }

    return (
      <CustomerSearchHistoryQueryComponent
        model={model}
        flexTemplateNode={flexTemplateNode}
        hotelsStore={hotelsStore}
      />
    );
  })
);

const CustomerSearchHistoryQueryComponent = (props: CustomerSearchHistoryQueryComponentProps) => {
  const { model, flexTemplateNode, hotelsStore } = props;
  const { formatText } = useLocalization();
  const { context } = useBexApi();
  const variables = {
    context,
    lobs: [HistoryType.PROPERTY_DETAIL],
    numberOfResults: NUMBER_OF_HOTELS_TO_GET,
  };

  const { data, loading, error } = useQuery<CustomerSearchHistoryResponse>(customerSearchHistoryQuery, {
    variables,
    ssr: false,
  });

  if (loading || error || !data) {
    return null;
  }

  const customerSearchHistory = data.customer.searchHistory
    ?.reduce((acc, searchHistoryItemsArray) => [...acc, ...searchHistoryItemsArray.items!], [])
    .filter((item) => item) as HistoryItem[];

  if (!customerSearchHistory) {
    return null;
  }

  // group by start date - end date
  const hotelsGroupedByDates: HotelsGroupedByDates = groupHotelsByDate(customerSearchHistory);

  const variablesList = buildVariablesList(hotelsGroupedByDates, context, hotelsStore!);

  const options = normalizeOptionsData(flexTemplateNode, model);
  const carouselClassContainer = customerSearchHistory.length < 4 ? "carouselFitItemsContainer" : "";
  const { title, seeAllLink } = model;
  const { id } = flexTemplateNode;

  return (
    <UitkSpacing padding={{ block: "three" }}>
      <div className="RecentlyViewedHotels" id={id} data-fm={options.fmId} data-fm-title-id={options.fmTitleId}>
        <UitkHeading tag="h2" size={4}>
          {title}
        </UitkHeading>
        <div data-testid="see-all-recently-viewed-link">
          <UitkLink>
            <TrackedLink moduleName="recently-viewed-hotels" href={seeAllLink} rfrr="link" linkName={seeAllLink}>
              {formatText("recentlyViewedHotels.seeAlllink")}
            </TrackedLink>
          </UitkLink>
        </div>
        <UitkSpacing padding={{ blockend: "three" }}>
          <div className={carouselClassContainer}>
            <IsomorphicCarousel
              itemsVisible={{
                lg: 3,
                md: 3,
                sm: 1,
              }}
              pageBy={1}
              peekWidth={50}
              buttonText={{
                nextButton: formatText("carousel.item.next"),
                prevButton: formatText("carousel.item.prev"),
              }}
              wrapItems={false}
            >
              {variablesList.map((variables, index: number) => (
                <RecentlyViewedHotelCardQueryComponent
                  key={`recently-viewed-hotels-${variables.hotelIds[0]}`}
                  cardIndex={index}
                  customerPropertySearchByIdVariables={variables}
                  hotelsStore={hotelsStore}
                  customerSearchHistory={customerSearchHistory}
                />
              ))}
            </IsomorphicCarousel>
          </div>
        </UitkSpacing>
      </div>
    </UitkSpacing>
  );
};

const customerPropertySearchByIdQuery = gql`
  ${customerPropertySearchByIdQueryQuery}
`;

export const RecentlyViewedHotelCardQueryComponent = (props: RecentlyViewedHotelsCardQueryComponentProps) => {
  const { cardIndex, customerPropertySearchByIdVariables, hotelsStore, customerSearchHistory } = props;

  const { data, loading, error } = useQuery<CustomerPropertySearchByIdResponse>(customerPropertySearchByIdQuery, {
    variables: customerPropertySearchByIdVariables,
    ssr: false,
  });

  if (loading || error || !data) {
    return null;
  }

  const recentViewedHotels = hotelsStore.getFreshRecentlyViewedHotels(customerSearchHistory, data);

  if (recentViewedHotels.length === 0) {
    return null;
  }

  return (
    <>
      {recentViewedHotels.map((recentlyViewedHotel) => (
        <RecentlyViewedHotelCard
          key={`recently-viewed-hotel-${recentlyViewedHotel.hisUrl}`}
          cardIndex={cardIndex}
          recentlyViewedHotel={recentlyViewedHotel}
        />
      ))}
    </>
  );
};

export default RecentlyViewedHotels;
